/**
 * API Keys Page
 * Manage API keys for programmatic access
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiKeysService, type ApiKey, type CreateApiKeyRequest } from '../services/api-keys.service'
import { formatDate } from '../utils/formatters'
import Layout from '../components/Layout'

export default function ApiKeysPage() {
  const queryClient = useQueryClient()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [createdKey, setCreatedKey] = useState<string | null>(null)
  const [editingKey, setEditingKey] = useState<ApiKey | null>(null)

  // Fetch API keys
  const { data: apiKeys = [], isLoading } = useQuery({
    queryKey: ['apiKeys'],
    queryFn: () => apiKeysService.list(),
  })

  // Create API key mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateApiKeyRequest) => apiKeysService.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] })
      setShowCreateModal(false)
      setCreatedKey(response.api_key.key) // Show full key once
    },
  })

  // Delete API key mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiKeysService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] })
    },
  })

  // Update API key mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: { label: string | null } }) =>
      apiKeysService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] })
      setEditingKey(null)
    },
  })

  const handleDelete = (id: number) => {
    if (window.confirm('確定要撤銷此 API Key 嗎？撤銷後將無法使用。')) {
      deleteMutation.mutate(id)
    }
  }

  return (
    <Layout>
      <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>API Keys 管理</h1>
          <p style={styles.subtitle}>管理您的程式化存取金鑰</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} style={styles.createButton}>
          + 建立 API Key
        </button>
      </div>

      {isLoading ? (
        <div style={styles.loading}>載入中...</div>
      ) : apiKeys.length === 0 ? (
        <div style={styles.empty}>
          <p style={styles.emptyText}>尚無 API Key</p>
          <p style={styles.emptySubtext}>建立第一個 API Key 以開始使用</p>
        </div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.tableHeader}>
                <th style={styles.th}>Key Prefix</th>
                <th style={styles.th}>標籤</th>
                <th style={styles.th}>權限</th>
                <th style={styles.th}>狀態</th>
                <th style={styles.th}>最後使用</th>
                <th style={styles.th}>建立時間</th>
                <th style={styles.th}>操作</th>
              </tr>
            </thead>
            <tbody>
              {apiKeys.map((key) => (
                <tr key={key.id} style={styles.tr}>
                  <td style={styles.td}>
                    <code style={styles.code}>{key.key_prefix}...</code>
                  </td>
                  <td style={styles.td}>
                    {editingKey?.id === key.id ? (
                      <input
                        type="text"
                        value={editingKey.label || ''}
                        onChange={(e) => setEditingKey({ ...editingKey, label: e.target.value })}
                        style={styles.editInput}
                        autoFocus
                      />
                    ) : (
                      key.label || <span style={styles.noLabel}>無標籤</span>
                    )}
                  </td>
                  <td style={styles.td}>
                    <div style={styles.permissions}>
                      {key.permissions.map((perm) => (
                        <span key={perm} style={styles.permissionBadge}>
                          {perm}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td style={styles.td}>
                    <span
                      style={{
                        ...styles.statusBadge,
                        ...(key.status === 'active'
                          ? styles.statusActive
                          : key.status === 'revoked'
                            ? styles.statusRevoked
                            : styles.statusExpired),
                      }}
                    >
                      {key.status === 'active' ? '有效' : key.status === 'revoked' ? '已撤銷' : '已過期'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    {key.last_used_at ? formatDate(key.last_used_at) : <span style={styles.noData}>從未</span>}
                  </td>
                  <td style={styles.td}>{formatDate(key.created_at)}</td>
                  <td style={styles.td}>
                    <div style={styles.actions}>
                      {editingKey?.id === key.id ? (
                        <>
                          <button
                            onClick={() =>
                              updateMutation.mutate({ id: key.id, data: { label: editingKey.label } })
                            }
                            style={styles.saveButton}
                            disabled={updateMutation.isPending}
                          >
                            儲存
                          </button>
                          <button onClick={() => setEditingKey(null)} style={styles.cancelButton}>
                            取消
                          </button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => setEditingKey(key)} style={styles.editButton}>
                            編輯
                          </button>
                          <button
                            onClick={() => handleDelete(key.id)}
                            style={styles.deleteButton}
                            disabled={deleteMutation.isPending || key.status !== 'active'}
                          >
                            撤銷
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create API Key Modal */}
      {showCreateModal && (
        <CreateApiKeyModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={(data) => createMutation.mutate(data)}
          isLoading={createMutation.isPending}
        />
      )}

      {/* Show Created Key Modal (only once) */}
      {createdKey && <CreatedKeyModal apiKey={createdKey} onClose={() => setCreatedKey(null)} />}
      </div>
    </Layout>
  )
}

/**
 * Create API Key Modal Component
 */
function CreateApiKeyModal({
  onClose,
  onSubmit,
  isLoading,
}: {
  onClose: () => void
  onSubmit: (data: CreateApiKeyRequest) => void
  isLoading: boolean
}) {
  const [formData, setFormData] = useState({
    label: '',
    permissions: ['read'],
  })

  const handlePermissionToggle = (perm: string) => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(perm)
        ? prev.permissions.filter((p) => p !== perm)
        : [...prev.permissions, perm],
    }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit({
      label: formData.label || undefined,
      permissions: formData.permissions,
    })
  }

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2 style={styles.modalTitle}>建立新 API Key</h2>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label}>標籤（選填）</label>
            <input
              type="text"
              value={formData.label}
              onChange={(e) => setFormData({ ...formData, label: e.target.value })}
              style={styles.input}
              placeholder="例如：生產環境 API Key"
              maxLength={50}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>權限</label>
            <div style={styles.checkboxGroup}>
              {['read', 'write', 'delete'].map((perm) => (
                <label key={perm} style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={formData.permissions.includes(perm)}
                    onChange={() => handlePermissionToggle(perm)}
                    style={styles.checkbox}
                  />
                  <span>{perm}</span>
                </label>
              ))}
            </div>
          </div>

          <div style={styles.modalActions}>
            <button type="button" onClick={onClose} style={styles.cancelButton} disabled={isLoading}>
              取消
            </button>
            <button
              type="submit"
              style={styles.submitButton}
              disabled={isLoading || formData.permissions.length === 0}
            >
              {isLoading ? '建立中...' : '建立'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

/**
 * Created Key Modal Component (shows full API key only once)
 */
function CreatedKeyModal({ apiKey, onClose }: { apiKey: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(apiKey)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2 style={styles.modalTitle}>API Key 已建立</h2>

        <div style={styles.warningBox}>
          <span style={styles.warningIcon}>⚠️</span>
          <div>
            <p style={styles.warningTitle}>請妥善保存此 API Key</p>
            <p style={styles.warningText}>這是唯一一次可以看到完整 API Key。離開此頁面後將無法再次查看。</p>
          </div>
        </div>

        <div style={styles.keyContainer}>
          <code style={styles.apiKeyCode}>{apiKey}</code>
          <button onClick={handleCopy} style={styles.copyButton}>
            {copied ? '已複製！' : '複製'}
          </button>
        </div>

        <button onClick={onClose} style={styles.closeButton}>
          我已安全保存
        </button>
      </div>
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '40px',
  },
  header: {
    maxWidth: '1200px',
    margin: '0 auto 30px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '16px',
    color: '#666',
  },
  createButton: {
    padding: '12px 24px',
    backgroundColor: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  loading: {
    textAlign: 'center',
    padding: '60px',
    fontSize: '16px',
    color: '#666',
  },
  empty: {
    maxWidth: '1200px',
    margin: '0 auto',
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '60px',
    textAlign: 'center',
  },
  emptyText: {
    fontSize: '18px',
    color: '#333',
    marginBottom: '8px',
  },
  emptySubtext: {
    fontSize: '14px',
    color: '#666',
  },
  tableContainer: {
    maxWidth: '1200px',
    margin: '0 auto',
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '20px',
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    borderBottom: '2px solid #e5e7eb',
  },
  th: {
    padding: '12px',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: '600',
    color: '#666',
    textTransform: 'uppercase',
  },
  tr: {
    borderBottom: '1px solid #f3f4f6',
  },
  td: {
    padding: '16px 12px',
    fontSize: '14px',
    color: '#333',
  },
  code: {
    fontFamily: 'monospace',
    backgroundColor: '#f3f4f6',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '13px',
  },
  noLabel: {
    color: '#999',
    fontStyle: 'italic',
  },
  noData: {
    color: '#999',
  },
  permissions: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
  },
  permissionBadge: {
    padding: '4px 8px',
    backgroundColor: '#dbeafe',
    color: '#1e40af',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '500',
  },
  statusBadge: {
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '500',
  },
  statusActive: {
    backgroundColor: '#dcfce7',
    color: '#166534',
  },
  statusRevoked: {
    backgroundColor: '#fee2e2',
    color: '#dc2626',
  },
  statusExpired: {
    backgroundColor: '#fef3c7',
    color: '#92400e',
  },
  actions: {
    display: 'flex',
    gap: '8px',
  },
  editButton: {
    padding: '6px 12px',
    backgroundColor: '#f3f4f6',
    color: '#333',
    border: 'none',
    borderRadius: '4px',
    fontSize: '12px',
    cursor: 'pointer',
  },
  deleteButton: {
    padding: '6px 12px',
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    border: 'none',
    borderRadius: '4px',
    fontSize: '12px',
    cursor: 'pointer',
  },
  saveButton: {
    padding: '6px 12px',
    backgroundColor: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '12px',
    cursor: 'pointer',
  },
  cancelButton: {
    padding: '6px 12px',
    backgroundColor: '#f3f4f6',
    color: '#333',
    border: 'none',
    borderRadius: '4px',
    fontSize: '12px',
    cursor: 'pointer',
  },
  editInput: {
    padding: '6px',
    border: '1px solid #2563eb',
    borderRadius: '4px',
    fontSize: '14px',
    width: '100%',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '30px',
    maxWidth: '500px',
    width: '90%',
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  modalTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    marginBottom: '20px',
    color: '#333',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#333',
  },
  input: {
    padding: '12px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '5px',
    outline: 'none',
  },
  checkboxGroup: {
    display: 'flex',
    gap: '16px',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '14px',
    cursor: 'pointer',
  },
  checkbox: {
    width: '16px',
    height: '16px',
    cursor: 'pointer',
  },
  modalActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '10px',
  },
  submitButton: {
    padding: '12px 24px',
    backgroundColor: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  warningBox: {
    padding: '16px',
    backgroundColor: '#fef3c7',
    borderRadius: '5px',
    marginBottom: '20px',
    display: 'flex',
    gap: '12px',
  },
  warningIcon: {
    fontSize: '20px',
  },
  warningTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#92400e',
    marginBottom: '4px',
  },
  warningText: {
    fontSize: '13px',
    color: '#92400e',
  },
  keyContainer: {
    display: 'flex',
    gap: '12px',
    marginBottom: '20px',
  },
  apiKeyCode: {
    flex: 1,
    padding: '12px',
    backgroundColor: '#f3f4f6',
    borderRadius: '5px',
    fontFamily: 'monospace',
    fontSize: '13px',
    wordBreak: 'break-all',
    border: '2px solid #2563eb',
  },
  copyButton: {
    padding: '12px 20px',
    backgroundColor: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  closeButton: {
    width: '100%',
    padding: '12px',
    backgroundColor: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
}
